import type { ControllerSummary } from './types';

/**
 * Which controller the console drives in a room, and where that choice is remembered.
 *
 * An operator once spent twenty minutes running a venue's real room from a lane's throwaway virtual Pi:
 * no scenes (a scene library is per-controller, on that controller's disk), a room drawing that never
 * moved, and a takeover that appeared to revert. Nothing was broken — the console had simply picked the
 * first device the cloud returned, which is Firestore document-id order, and the real Pi sorted last.
 *
 * So the rule here is that the console does not guess. It is the whole subject of this module rather
 * than three lines inside a load function, because every entry into a room runs through it.
 */

/**
 * Where a room's chosen controller is remembered — per ROOM, not per browser.
 *
 * A console that remembered one controller globally carried the id of the room it was last in into
 * whatever room it opened next, and a room with several controllers then had no way to tell "the
 * operator picked this one" from "this is left over from somewhere else". A direct venue-network or
 * Bluetooth console has no cloud room and keys under `local`.
 */
export function controllerStorageKey(venueId: string, roomId: string): string {
  return `nuance-venue:device:${venueId || 'local'}:${roomId || 'local'}`;
}

export function rememberedControllerId(venueId: string, roomId: string): string {
  return localStorage.getItem(controllerStorageKey(venueId, roomId)) ?? '';
}

/** Record the operator's choice for this room; an empty id forgets it. */
export function rememberController(venueId: string, roomId: string, deviceId: string): void {
  const key = controllerStorageKey(venueId, roomId);
  if (deviceId) localStorage.setItem(key, deviceId);
  else localStorage.removeItem(key);
}

/**
 * The controller to drive this room from, given what the room actually contains.
 *
 * The operator's own remembered choice wins while that controller is still bound to the room. A room
 * holding exactly ONE live controller selects it, because there is nothing to choose. Anything else —
 * two or more live controllers with no remembered choice, or a remembered controller that has since
 * left the room — selects NOTHING and leaves the choice to the Connect screen's list. A revoked
 * controller is never a candidate, remembered or not.
 *
 * Ordering the cloud's list (which `getRoomStatus` now does) is what makes that list legible to a
 * person. Only refusing to auto-pick makes a wrong controller impossible to inherit in silence.
 */
export function resolveControllerForRoom(
  devices: readonly ControllerSummary[],
  venueId: string,
  roomId: string,
): string {
  const live = devices.filter((device) => device.status !== 'revoked');
  const remembered = rememberedControllerId(venueId, roomId);
  if (remembered && live.some((device) => device.deviceId === remembered)) return remembered;
  return live.length === 1 ? live[0]!.deviceId : '';
}

/** What a direct connection says about itself: which controller, and which room it is claimed into. */
export interface DirectControllerHello {
  deviceId: string;
  venueId?: string;
  roomId?: string;
}

/** Where the console is pointed right now. */
export interface ConsoleSelection {
  venueId: string;
  roomId: string;
  deviceId: string;
}

/**
 * Where the console is pointed after a direct connection has had its say, and where — if anywhere —
 * that choice belongs in storage.
 */
export interface SelectionOutcome extends ConsoleSelection {
  /**
   * The room to remember this controller for. It is the room the CONTROLLER names, never the room the
   * console happened to be showing, and it is absent when the controller names none: a bench Pi paired
   * over Bluetooth must not be written into the remembered choice of whatever venue room was on screen.
   */
  remember?: { venueId: string; roomId: string };
}

function text(value: string | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The selection after an operator deliberately pairs with a controller — typing its address, entering
 * its setup code, or opening the controller's own console.
 *
 * Reaching for a specific controller IS a choice, so it is taken as one: the console drives that
 * controller, follows it to the room it is claimed into, and remembers it for THAT room, so opening
 * the room again later lands on the same controller rather than on whatever the cloud lists first.
 */
export function selectionAfterPairing(
  hello: DirectControllerHello,
  current: ConsoleSelection,
): SelectionOutcome {
  const deviceId = text(hello.deviceId);
  const venueId = text(hello.venueId);
  const roomId = text(hello.roomId);
  return {
    deviceId: deviceId || current.deviceId,
    venueId: venueId || current.venueId,
    roomId: roomId || current.roomId,
    remember: deviceId && venueId && roomId ? { venueId, roomId } : undefined,
  };
}

/**
 * The selection after a controller re-announces itself on a connection that is already open.
 *
 * A `hello` arriving on its own is not an operator action — it is a daemon that restarted, or whose
 * claim changed, saying so. It used to overwrite the selection unconditionally, which meant a warm
 * direct link could silently move the console onto a different controller, in a different room, from
 * the one the operator had chosen; every screen then addressed that one without saying so. So an
 * unasked-for hello may only speak for the controller already driving this console, or fill the seat
 * when nothing holds it AND the controller belongs to the room on screen. Otherwise it changes nothing.
 */
export function selectionAfterHello(
  hello: DirectControllerHello,
  current: ConsoleSelection,
): SelectionOutcome {
  const held: SelectionOutcome = { ...current, remember: undefined };
  const deviceId = text(hello.deviceId);
  if (!deviceId) return held;
  if (deviceId === current.deviceId) return selectionAfterPairing(hello, current);
  if (!current.deviceId && namesRoom(hello, current)) return selectionAfterPairing(hello, current);
  return held;
}

/**
 * Whether the controller is claimed into the room the console is showing. A controller that names no
 * room at all only matches a console that is not in one either — a direct console over a cable, which
 * has no cloud room to be wrong about.
 */
function namesRoom(hello: DirectControllerHello, current: ConsoleSelection): boolean {
  const venueId = text(hello.venueId);
  const roomId = text(hello.roomId);
  if (!venueId && !roomId) return !current.venueId && !current.roomId;
  return venueId === current.venueId && roomId === current.roomId;
}
